import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { compose } from 'redux';
import { connect } from 'react-redux';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { sendChatMessage, addChat, getChats, renameChat, getChatMessages, deleteChat, shareChat, resetActiveChat, setActiveChat } from '../../store/actions/chatbotActions';
import { getUsers } from "../../store/actions/adminActions";
import TopBar from "../../components/TopBar";
import Sidebar from "../../components/Sidebar";
import { Redirect } from "react-router-dom";
import {
  PanelLeft,
  MessageSquare,
  Folder,
  Shapes,
  Star,
  Search,
  ChevronDown,
  Plus,
  AlertTriangle,
  Shield,
  Lock,
  FileText,
  BadgeCheck,
  Database,
  Building2,
  ChevronUp,
  MessageSquareText,
  MoreHorizontal,
  Pencil,
  Trash2,
  Share2,
} from 'lucide-react';
import RaboBotAvatar from '../../assets/images/FINANDFIND.png';

function Avatar({ name, src, size = 28 }) {
  const [errored, setErrored] = React.useState(false);

  const initials = (name || "")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  if (!src || errored) {
    return (
      <div
        className="flex items-center justify-center rounded-full border border-gray-300 bg-gray-100 text-gray-500"
        style={{ width: size, height: size }}
        title={name}
      >
        <svg
          width={Math.floor(size * 0.6)}
          height={Math.floor(size * 0.6)}
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
          fill="currentColor"
        >
          <path d="M10 10a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm0 1c-3.33 0-6 1.34-6 3v2h12v-2c0-1.66-2.67-3-6-3Z" />
        </svg>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={name}
      title={name}
      loading="lazy"
      referrerPolicy="no-referrer"
      className="rounded-full object-cover border border-gray-300 bg-white"
      style={{ width: size, height: size }}
      onError={() => setErrored(true)}
    />
  );
}

const ChatPage = ({ token, messages, resetActiveChatFN, activeChatIdRaw, setActiveChatFN, setActiveChat, loading, sendChatMessageFN, addChatFN, renameChatFN, getChatsFN, chats, getChatMessagesFN, profile, shareChatFN, deleteChatFN, getUsersFN, users }) => {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);
  const dispatch = useDispatch();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };


  const [shareSuccess, setShareSuccess] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [currentChatId, setCurrentChatId] = useState(null)
  const [showAllPrompts, setShowAllPrompts] = useState(false);
  const [visibleChats, setVisibleChats] = useState(null);

  const [openMenuChatId, setOpenMenuChatId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });


  const [renamingChatId, setRenamingChatId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const [shareChatId, setShareChatId] = useState(null);
  const [shareSearch, setShareSearch] = useState('');

  const currentChatIdRef = useRef(null);

  const filteredShareUsers = (users || []).filter((user) => {
    const fullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
    const email = (user.email || '').toLowerCase();
    const q = shareSearch.toLowerCase();

    return fullName.includes(q) || email.includes(q);
  });

  useEffect(() => {
    if (activeChatIdRaw) {
      setCurrentChatId(activeChatIdRaw)
    }
  }, [activeChatIdRaw]);


  useEffect(() => {
    currentChatIdRef.current = currentChatId;
  }, [currentChatId]);

  /** `promptMessage` stays English so the first user message sent to the API is unchanged. */
  const promptSuggestions = useMemo(
    () => [
      { icon: AlertTriangle, promptId: 'riskReport', promptMessage: 'Risk report - Do you wonder about your risk report?' },
      { icon: Shield, promptId: 'compliance', promptMessage: 'Compliance - Need help with compliance requirements?' },
      { icon: Lock, promptId: 'gdpr', promptMessage: 'GDPR - Questions about GDPR compliance?' },
      { icon: FileText, promptId: 'insuranceRegulation', promptMessage: 'Insurance regulation - What is new regulation in insurance sector?' },
      { icon: BadgeCheck, promptId: 'iso27001', promptMessage: 'ISO 27001 - Learn about ISO 27001 standards' },
      { icon: MessageSquareText, promptId: 'regulationOpinion', promptMessage: 'Regulation opinion - Get expert opinion on regulations' },
      { icon: Database, promptId: 'kvkk', promptMessage: 'KVKK - KVKK compliance and requirements' },
      { icon: Building2, promptId: 'masak', promptMessage: 'MASAK - MASAK regulations and compliance' },
    ],
    []
  );

  const startRenameChat = (chat) => {
    setOpenMenuChatId(null);
    setRenamingChatId(chat.chat_id);
    setRenameValue(chat.title || '');
  };

  const submitRenameChat = async () => {
    const finalTitle = renameValue.trim();
    if (!renamingChatId || !finalTitle) {
      setRenamingChatId(null);
      setRenameValue('');
      return;
    }

    await renameChatFN(renamingChatId, finalTitle.slice(0, 60));
    setRenamingChatId(null);
    setRenameValue('');
  };

  const cancelRenameChat = () => {
    setRenamingChatId(null);
    setRenameValue('');
  };

  const handleNewChat = async () => {
    try {
      const res = await addChatFN("New Chat");
      const newChatId = res.chat_id;

      dispatch({ type: "CHATBOT_CLEAR_MESSAGES" });

      currentChatIdRef.current = newChatId;
      setCurrentChatId(newChatId);
      setActiveChatFN(newChatId)

      await getChatsFN();
    } catch (err) {
      console.error("Failed to create chat:", err);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);


  useEffect(() => {
    getChatsFN();
  }, [getChatsFN]);

  useEffect(() => {
    if (chats) {
      //console.log("chats ", chats)
      setVisibleChats(chats)

    }
  }, [chats]);

  useEffect(() => {
    getUsersFN();
  }, [getUsersFN]);

  useEffect(() => {
    if (!shareSuccess) return;

    const timer = setTimeout(() => {
      setShareSuccess(false);
    }, 2200);

    return () => clearTimeout(timer);
  }, [shareSuccess]);

  const handleSubmit = async (e, promptOverride = null) => {
    e.preventDefault();

    const finalMessage = (promptOverride ?? input).trim();
    if (!finalMessage) return;

    dispatch({
      type: 'CHATBOT_MESSAGE_SEND_USER',
      payload: {
        type: 'user',
        text: finalMessage,
        sender: 'user',
        senderName: 'You',
        time: new Date().toLocaleTimeString([], {
          hour: 'numeric',
          minute: '2-digit',
        }),
      },
    });

    setInput('');

    try {
      let activeChatId = currentChatIdRef.current;

      if (!activeChatId) {
        const res = await addChatFN('New Chat');
        activeChatId = res.chat_id;

        currentChatIdRef.current = activeChatId;
        setCurrentChatId(activeChatId);
        setActiveChatFN(activeChatId)
        await getChatsFN();
      }

      const result = await dispatch(sendChatMessage(finalMessage, activeChatId));

      const resolvedChatId = result?.chatId || activeChatId;

      if (resolvedChatId && currentChatIdRef.current !== resolvedChatId) {
        currentChatIdRef.current = resolvedChatId;
        setCurrentChatId(resolvedChatId);
        setActiveChatFN(resolvedChatId)
      }

      const activeChat = (visibleChats || []).find(
        (chat) => chat.chat_id === resolvedChatId
      );

      if (!activeChat || (activeChat.title || '').trim() === 'New Chat') {
        await renameChatFN(resolvedChatId, finalMessage.slice(0, 60));
        await getChatsFN();
      }
    } catch (err) {
      console.error('Failed sending chat message:', err);
    }
  };

  const TypingDots = () => (
    <div className="flex items-center gap-1 pt-1">
      <span className="w-2 h-2 bg-[#9CA3AF] rounded-full animate-bounce [animation-delay:-0.3s]" />
      <span className="w-2 h-2 bg-[#9CA3AF] rounded-full animate-bounce [animation-delay:-0.15s]" />
      <span className="w-2 h-2 bg-[#9CA3AF] rounded-full animate-bounce" />
    </div>
  );

  const handlePromptClick = (promptMessage) => {
    setInput(promptMessage);

    setTimeout(() => {
      handleSubmit(
        {
          preventDefault: () => { },
        },
        promptMessage
      );
    }, 0);
  };

  const activeShareChat = (visibleChats || []).find(
    (chat) => chat.chat_id === shareChatId
  );

  const sharedUsers = Array.isArray(activeShareChat?.users)
    ? activeShareChat.users
    : typeof activeShareChat?.users === 'string'
      ? JSON.parse(activeShareChat.users)
      : [];

  const sharedUserIds = sharedUsers.map((user) => user.user_id);

  return token ? (
    <div className="w-full h-screen flex flex-col bg-[#F8F8F8] overflow-hidden">
      <div className="flex-shrink-0">
        <TopBar />
      </div>

      <div className="flex flex-1 overflow-hidden min-h-0">
        <Sidebar />

        <div className="flex flex-1 overflow-hidden min-h-0">

          {/* Chat history sidebar */}
          <div className="relative h-full">
            {/* Re-open button when sidebar is hidden */}
            {!sidebarOpen && (
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className="absolute left-4 top-4 z-30 w-9 h-9 rounded-md border border-[#E5E7EB] bg-white hover:bg-[#F9FAFB] flex items-center justify-center shadow-sm transition"
              >
                <PanelLeft size={16} className="text-[#6B7280]" />
              </button>
            )}

            {/* Chat history sidebar */}
            <div
              className={`
      h-full bg-[#FAFAFA] border-r border-[#E5E7EB] flex flex-col
      transition-all duration-300 overflow-hidden
      ${sidebarOpen ? 'w-[260px] opacity-100' : 'w-0 opacity-0 border-r-0'}
    `}
            >
              {/* Top row */}
              <div className="h-[64px] flex items-center justify-between px-4 border-b border-[#E5E7EB]">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-[18px] font-semibold text-[#2C2C2C] truncate">
                    {t('askRaboPage.title')}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() => setSidebarOpen(false)}
                  className="w-8 h-8 rounded-md hover:bg-[#F3F4F6] flex items-center justify-center transition"
                >
                  <PanelLeft size={16} className="text-[#6B7280]" />
                </button>
              </div>

              {/* New chat */}
              <div className="p-3">
                <button
                  onClick={handleNewChat}
                  className="w-full h-[40px] rounded-[10px] bg-[#F3F4F6] border border-[#E5E7EB] hover:bg-[#ECEFF3] transition text-[#2C2C2C] flex items-center justify-start px-4 gap-2"
                >
                  <Plus size={16} />
                  <span className="text-[13px]">{t('askRaboPage.newChat')}</span>
                </button>
              </div>

              {/* Main nav */}
              <div className="px-2 space-y-1">
                <button className="w-full h-[40px] rounded-[10px] hover:bg-[#F3F4F6] transition flex items-center text-[#4B5563] px-3 gap-3 justify-start">
                  <MessageSquare size={16} />
                  <span className="text-[13px]">{t('askRaboPage.navChats')}</span>
                </button>

                <button className="w-full h-[40px] rounded-[10px] hover:bg-[#F3F4F6] transition flex items-center text-[#4B5563] px-3 gap-3 justify-start">
                  <Folder size={16} />
                  <span className="text-[13px]">{t('askRaboPage.navProjects')}</span>
                </button>

                <button className="w-full h-[40px] rounded-[10px] hover:bg-[#F3F4F6] transition flex items-center text-[#4B5563] px-3 gap-3 justify-start">
                  <Shapes size={16} />
                  <span className="text-[13px]">{t('askRaboPage.navArtifacts')}</span>
                </button>
              </div>

              <div className="px-4 pt-6 pb-2 text-[11px] font-semibold text-[#9CA3AF] tracking-wide">
                {t('askRaboPage.recents')}
              </div>

              <div className="flex-1 overflow-y-auto px-2 pb-4 space-y-1">
                {(visibleChats || []).map((chat) => (
                  <div key={chat.chat_id} className="relative">
                    <div
                      className={`w-full rounded-[10px] transition ${chat.chat_id === currentChatId
                          ? 'bg-[#F3F4F6] text-[#2C2C2C]'
                          : 'text-[#6B7280] hover:bg-[#F3F4F6]'
                        } px-3 py-2 pr-10 text-left text-[13px]`}
                    >
                      {renamingChatId === chat.chat_id ? (
                        <input
                          autoFocus
                          value={renameValue}
                          maxLength={60}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          onBlur={submitRenameChat}
                          onKeyDown={async (e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              await submitRenameChat();
                            }
                            if (e.key === 'Escape') {
                              e.preventDefault();
                              cancelRenameChat();
                            }
                          }}
                          className="w-full bg-transparent outline-none text-[13px] text-[#2C2C2C] placeholder:text-[#9CA3AF]"
                          placeholder={t('askRaboPage.renameChatPlaceholder')}
                        />
                      ) : (
                        <button
                          onClick={async () => {
                            setCurrentChatId(chat.chat_id);
                            setActiveChatFN(chat.chat_id);
                            setOpenMenuChatId(null);
                            await getChatMessagesFN(chat.chat_id);
                          }}
                          className="block w-full text-left"
                        >
                          <span className="block truncate">
                            {chat.title || t('askRaboPage.defaultChatTitle')}
                          </span>
                        </button>
                      )}
                    </div>

                    {renamingChatId !== chat.chat_id && (
                      <div className="absolute right-2 top-1/2 -translate-y-1/2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();

                            const rect = e.currentTarget.getBoundingClientRect();

                            setMenuPosition({
                              top: rect.bottom + 6,
                              left: rect.right - 180,
                            });

                            setOpenMenuChatId((prev) =>
                              prev === chat.chat_id ? null : chat.chat_id
                            );
                          }}
                          className="w-7 h-7 rounded-md flex items-center justify-center text-[#9CA3AF] hover:text-[#374151] hover:bg-white"
                        >
                          <MoreHorizontal size={15} />
                        </button>
                      </div>
                    )}

                    {openMenuChatId === chat.chat_id && (
                      <div
                        className="fixed z-[99999] min-w-[180px] overflow-hidden rounded-[12px] border border-[#E5E7EB] bg-white shadow-[0_10px_30px_rgba(0,0,0,0.14)]"
                        style={{
                          top: `${menuPosition.top}px`,
                          left: `${menuPosition.left}px`,
                          backgroundColor: '#FFFFFF',
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() => startRenameChat(chat)}
                          className="flex w-full items-center gap-3 bg-white px-3 py-2.5 text-left text-[13px] text-[#374151] hover:bg-[#F9FAFB]"
                        >
                          <Pencil size={15} className="text-[#6B7280]" />
                          <span>{t('askRaboPage.rename')}</span>
                        </button>

                        <button
                          onClick={() => {
                            setOpenMenuChatId(null);
                            deleteChatFN(chat.chat_id);
                            setCurrentChatId(null);
                            resetActiveChatFN();
                          }}
                          className="flex w-full items-center gap-3 bg-white px-3 py-2.5 text-left text-[13px] text-[#DC2626] hover:bg-[#FEF2F2]"
                        >
                          <Trash2 size={15} className="text-[#DC2626]" />
                          <span>{t('askRaboPage.delete')}</span>
                        </button>

                        <button
                          onClick={() => {
                            setOpenMenuChatId(null);
                            setShareChatId(chat.chat_id);
                            setShareSearch('');
                          }}
                          className="flex w-full items-center gap-3 bg-white px-3 py-2.5 text-left text-[13px] text-[#374151] hover:bg-[#F9FAFB]"
                        >
                          <Share2 size={15} className="text-[#6B7280]" />
                          <span>{t('askRaboPage.share')}</span>
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Bottom user row */}
              <div className="border-t border-[#E5E7EB] px-2 h-16 flex items-center">
                <button className="w-full flex items-center text-left hover:bg-[#F3F4F6] rounded-[10px] transition px-2 py-2 gap-3">
                  <div className="flex items-center gap-2 w-full">
                    <Avatar
                      name={profile?.name || profile?.email}
                      src={profile?.avatar}
                      size={28}
                    />

                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] text-[#2C2C2C] truncate">
                        {profile?.name || profile?.email || t('askRaboPage.userFallback')}
                      </div>

                      <div className="text-[11px] text-[#9CA3AF]">
                        {profile?.plan || t('askRaboPage.planFallback')}
                      </div>
                    </div>

                    <ChevronDown size={13} className="text-[#9CA3AF]" />
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Main chat */}
          <div className="flex-1 min-w-0 flex flex-col min-h-0">

           

            {/* Content */}
            <div className="flex-1 min-h-0 flex flex-col">
              {messages?.length === 0 && !loading ? (
                <div className="h-full flex items-center justify-center px-6">
                  <div className="w-full max-w-[900px] flex flex-col items-center">
                    <h1 className="text-[42px] font-semibold text-[#2C2C2C] text-center">
                      {t('askRaboPage.welcomeTitle')}
                    </h1>

                    <p className="text-[13px] text-[#8A8A8A] mt-3 text-center">
                      {t('askRaboPage.welcomeSubtitle')}
                    </p>

                    <form onSubmit={handleSubmit} className="w-full mt-10">
                      <div className="min-h-[70px] rounded-[24px] border border-[#D8D8D8] bg-white px-5 flex items-center gap-3">

                        <input
                          type="text"
                          value={input}
                          onChange={(e) => setInput(e.target.value)}
                          placeholder={t('askRaboPage.inputPlaceholder')}
                          className="flex-1 bg-transparent outline-none text-[15px]"
                        />

                        <button
                          type="submit"
                          disabled={!input.trim()}
                          className={`w-10 h-10 rounded-full flex items-center justify-center text-white transition
      ${input.trim() ? 'bg-[#3B82F6] hover:bg-[#2563EB]' : 'bg-gray-300 cursor-not-allowed'}
    `}
                        >
                          <i className="fas fa-paper-plane text-[14px]" />
                        </button>

                      </div>
                    </form>

                    <div className="w-full mt-8">
                      {(showAllPrompts ? promptSuggestions : promptSuggestions.slice(0, 3)).map((item, idx) => {
                        const Icon = item.icon;
                        const title = t(`askRaboPage.prompts.${item.promptId}.title`);
                        const text = t(`askRaboPage.prompts.${item.promptId}.text`);

                        return (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => handlePromptClick(item.promptMessage)}
                            className="w-full flex items-center gap-3 px-2 py-3 text-left hover:bg-[#F7F7F7] rounded-[10px] transition"
                          >
                            <Icon size={17} className="text-[#6B7280] shrink-0" />
                            <div className="min-w-0">
                              <span className="text-[15px] font-semibold text-[#2C2C2C]">
                                {title}
                              </span>
                              <span className="text-[15px] text-[#6B7280]">
                                {' '} - {text}
                              </span>
                            </div>
                          </button>
                        );
                      })}

                      {promptSuggestions.length > 3 && (
                        <div className="flex justify-center mt-4">
                          <button
                            type="button"
                            onClick={() => setShowAllPrompts(prev => !prev)}
                            className="inline-flex items-center gap-2 text-[14px] text-[#6B7280] hover:text-[#2C2C2C] transition"
                          >
                            {showAllPrompts
                              ? t('askRaboPage.showLess')
                              : t('askRaboPage.showMoreWithCount', { count: promptSuggestions.length - 3 })}
                            {showAllPrompts ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {/* Messages scroll */}
                  <div className="flex-1 overflow-y-auto min-h-0">
                    <div className="w-full max-w-[900px] mx-auto px-6 py-8 flex flex-col gap-4">
                      {messages.map((msg, idx) => (
                        <div
                          key={idx}
                          className={`w-full rounded-[6px] px-6 py-5 ${(msg.sender || msg.type) === 'bot' ? 'bg-[#F7F7F7]' : ''
                            }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 shrink-0 flex items-center justify-center">
                              {(msg.sender || msg.type) === 'user' ? (
                                <Avatar
                                  name={`${profile?.first_name || ''} ${profile?.last_name || ''}`.trim()}
                                  src={profile?.avatar}
                                  size={32}
                                />
                              ) : (
                                <img
                                  src={RaboBotAvatar}
                                  alt="Rabo"
                                  className="w-8 h-8 rounded-full object-cover border border-[#E5E7EB]"
                                />
                              )}
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-[14px] font-medium text-[#2C2C2C]">
                                  {msg.senderName || ((msg.sender || msg.type) === 'user' ? t('askRaboPage.messageYou') : 'Rabo')}
                                </span>

                                <span className="text-[12px] text-[#9CA3AF]">
                                  {msg.time || ''}
                                </span>
                              </div>

                              <div className="text-[15px] leading-7 text-[#2C2C2C]">
                                {(msg.sender || msg.type) === 'bot' ? (
                                  msg.text ? (
                                    <ReactMarkdown
                                      remarkPlugins={[remarkGfm]}
                                      rehypePlugins={[rehypeHighlight]}
                                      className="prose prose-sm max-w-none"
                                    >
                                      {msg.text}
                                    </ReactMarkdown>
                                  ) : (
                                    <TypingDots />
                                  )
                                ) : (
                                  msg.text
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}

                      <div ref={messagesEndRef} />
                    </div>
                  </div>

                  <div className="px-6 pb-6">
                    <form onSubmit={handleSubmit} className="w-full max-w-[900px] mx-auto">
                      <div className="min-h-[70px] rounded-[24px] border border-[#D8D8D8] bg-white px-5 flex items-center gap-3">

                        <input
                          type="text"
                          value={input}
                          onChange={(e) => setInput(e.target.value)}
                          placeholder={t('askRaboPage.inputPlaceholder')}
                          className="flex-1 bg-transparent outline-none text-[15px]"
                        />

                        {/* SEND BUTTON */}
                        <button
                          type="submit"
                          disabled={!input.trim()}
                          className={`w-10 h-10 rounded-full flex items-center justify-center text-white transition
    ${input.trim() ? 'bg-[#3B82F6] hover:bg-[#2563EB]' : 'bg-gray-300 cursor-not-allowed'}
  `}
                        >
                          <i className="fas fa-paper-plane text-[14px]" />
                        </button>

                      </div>
                    </form>
                  </div>

                </>
              )}
            </div>
          </div>
          {shareChatId && (
            <div
              className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/30"
              onClick={() => {
                setShareChatId(null);
                setShareSearch('');
              }}
            >
              <div
                className="w-full max-w-[420px] rounded-[16px] bg-white shadow-[0_20px_60px_rgba(0,0,0,0.18)] border border-[#E5E7EB] p-4"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="mb-3">
                  <div className="text-[16px] font-semibold text-[#111827]">{t('askRaboPage.shareModalTitle')}</div>
                  <div className="text-[13px] text-[#6B7280] mt-1">
                    {t('askRaboPage.shareModalSubtitle')}
                  </div>
                </div>

                <input
                  value={shareSearch}
                  onChange={(e) => setShareSearch(e.target.value)}
                  placeholder={t('askRaboPage.shareSearchPlaceholder')}
                  className="w-full h-[40px] rounded-[10px] border border-[#E5E7EB] px-3 text-[13px] outline-none focus:border-[#D1D5DB]"
                />

                <div className="mt-3 max-h-[280px] overflow-y-auto space-y-1">
                  {filteredShareUsers.map((user) => {
                    const activeShareChat = (visibleChats || []).find(
                      (chat) => chat.chat_id === shareChatId
                    );

                    const sharedUsers = Array.isArray(activeShareChat?.users)
                      ? activeShareChat.users
                      : typeof activeShareChat?.users === 'string'
                        ? JSON.parse(activeShareChat.users)
                        : [];

                    const isAlreadyShared = sharedUsers.some(
                      (u) => u.user_id === user.user_id
                    );

                    return (
                      <button
                        key={user.user_id}
                        disabled={isAlreadyShared}
                        onClick={async () => {
                          if (isAlreadyShared) return;

                          try {
                            await shareChatFN(shareChatId, user.user_id);
                            setShareSuccess(true);
                            setShareChatId(null);
                            setShareSearch('');
                            await getChatsFN();
                          } catch (err) {
                            console.error('Failed to share chat:', err);
                          }
                        }}
                        className={`w-full flex items-center gap-3 rounded-[10px] px-3 py-2 text-left ${isAlreadyShared
                          ? 'cursor-default bg-[#F9FAFB] opacity-70'
                          : 'hover:bg-[#F9FAFB]'
                          }`}
                      >
                        <Avatar
                          name={`${user.first_name || ''} ${user.last_name || ''}`.trim()}
                          src={user.avatar}
                          size={32}
                        />

                        <div className="min-w-0 flex-1">
                          <div className="truncate text-[13px] font-medium text-[#111827]">
                            {`${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unnamed User'}
                          </div>
                          <div className="truncate text-[12px] text-[#6B7280]">
                            {user.email}
                          </div>
                        </div>

                        {isAlreadyShared && (
                          <span className="shrink-0 rounded-full bg-[#EEF2FF] px-2 py-1 text-[11px] font-medium text-[#4F46E5]">
                            {t('askRaboPage.alreadyShared')}
                          </span>
                        )}
                      </button>
                    );
                  })}

                  {!filteredShareUsers.length && (
                    <div className="px-3 py-6 text-center text-[13px] text-[#9CA3AF]">
                      {t('askRaboPage.noUsersFound')}
                    </div>
                  )}
                </div>

                <div className="mt-4 flex justify-end">
                  <button
                    onClick={() => {
                      setShareChatId(null);
                      setShareSearch('');
                    }}
                    className="h-[36px] rounded-[10px] border border-[#E5E7EB] px-4 text-[13px] text-[#374151] hover:bg-[#F9FAFB]"
                  >
                    {t('askRaboPage.close')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {shareSuccess && (
            <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[120000]">
              <div className="flex items-center gap-2 rounded-[12px] bg-[#16A34A] px-4 py-3 text-[13px] text-white shadow-[0_10px_30px_rgba(0,0,0,0.18)]">
                <span className="text-[14px]">✓</span>
                <span>{t('askRaboPage.chatSharedSuccess')}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>

  ) : (
    <Redirect to="/login" />
  );
};

const mapStateToProps = (state) => {
  //console.log( state.chatbot.messages)
  return {
    token: state.auth.token,
    messages: state.chatbot.messages || [],
    loading: state.chatbot.loading,
    chats: state.chatbot.chats || [],
    profile: state.profile.profile,
    users: state.admin.admin_users,
    activeChatIdRaw: state.chatbot.activeChatId
  }
};

const mapDispatchToProps = (dispatch) => ({
  sendChatMessageFN: (message) => dispatch(sendChatMessage(message)),
  addChatFN: (title) => dispatch(addChat(title)),
  getChatsFN: () => dispatch(getChats()),
  renameChatFN: (id, title) => dispatch(renameChat(id, title)),
  getChatMessagesFN: (id) => dispatch(getChatMessages(id)),
  deleteChatFN: (id) => dispatch(deleteChat(id)),
  getUsersFN: () => dispatch(getUsers()),
  shareChatFN: (chat_id, user_id) => shareChat(chat_id, user_id),
  resetActiveChatFN: () => dispatch(resetActiveChat()),
  setActiveChatFN: (id) => dispatch(setActiveChat(id))
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps)
)(ChatPage);